import type { ScrollViewInstance, TextInputInstance } from 'react-native';
import { useState, useRef, useEffect } from 'react'
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    Platform,
    ActivityIndicator,
    KeyboardAvoidingView,
    ScrollView,
} from 'react-native'
import { FontAwesome6 } from '@expo/vector-icons';
import { signOutCurrentSession, supabase } from '../lib/supabase'
import { useSession } from '../lib/SessionContext';
import { useAuth } from '../lib/AuthContext';
import { captureExceptionIfNotNetwork } from '../lib/sentryUtils';
import {
    getLastEmail,
    hasEverLoggedInHint,
    markAsLoggedIn,
    saveLastEmail,
} from '../utils/firstTimeUser';
import VerificationCodePrompt from './VerificationCodePrompt';
import colors from '../lib/colors';
import { getOrCreateSignupAttribution } from '../lib/signupAttribution';
import { type PartialFunnelContext } from '../lib/funnelContext';
import { usePathname, useRouter } from 'expo-router';
import { getE2ETestRunContext } from '../lib/e2eTestRun';
import { getClientSessionMetadata } from '../lib/clientSessionMetadata';
import {
    persistPasswordlessSessionState,
    resolvePasswordlessAuthOutcome,
    sendPasswordlessEmailCode,
} from '../lib/passwordlessEmail';
import { useResendCooldown } from '../hooks/useResendCooldown';
import { isValidEmailAddress, normalizeEmailAddress } from '../utils/email';
import { Trans } from '@lingui/react/macro';
import { msg, plural } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../lib/i18n/AppI18nProvider';
import {
    isPublicUrlLocaleAuthoritative,
    stripStaticPublicExportPrefix,
} from '../lib/i18n/publicWeb';
import { buildExplicitSignupLanguageMetadata } from '../lib/i18n/signupLanguage';

interface AuthScreenProps {
    mode?: 'signin' | 'signup'
    onVerificationCodeSent?: () => void
    redirectTo?: string // URL to redirect to after login
    forSwitchAccount?: boolean // Sign out current user before sending a verification code
    funnelContext?: PartialFunnelContext
    initialEmail?: string
    copyVariant?: 'default' | 'forwarded' | 'legacy-offer'
    signInOnly?: boolean
}

export default function AuthScreen({
    mode,
    onVerificationCodeSent,
    redirectTo,
    forSwitchAccount,
    funnelContext,
    initialEmail,
    copyVariant = 'default',
    signInOnly = false,
}: AuthScreenProps = {}) {
    const { i18n } = useLingui();
    const { isReturningUser } = useSession();
    const { closeAuth, setConfirmation } = useAuth();
    const router = useRouter();
    const pathname = usePathname();
    const { messageLocale } = useAppLocale();
    const resolvedMode = signInOnly
        ? 'signin'
        : mode || ((isReturningUser || hasEverLoggedInHint()) ? 'signin' : 'signup');
    const [selectedMode, setSelectedMode] = useState<'signin' | 'signup'>(resolvedMode);
    const requireFirstName = !signInOnly && selectedMode === 'signup' && !forSwitchAccount;
    const isForwardedAuth = copyVariant === 'forwarded';
    const isLegacyOfferAuth = copyVariant === 'legacy-offer';
    const [firstName, setFirstName] = useState('')
    const normalizedInitialEmail = initialEmail ? normalizeEmailAddress(initialEmail) : ''
    const [email, setEmail] = useState(normalizedInitialEmail)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<MessageDescriptor | null>(null)
    const [success, setSuccess] = useState<MessageDescriptor | null>(null)
    const [verificationCodeSent, setVerificationCodeSent] = useState(false)
    const { resendCooldown, setResendCooldown, startResendCooldown } = useResendCooldown()
    const firstNameRef = useRef<TextInputInstance>(null)
    const emailRef = useRef<TextInputInstance>(null)
    const scrollViewRef = useRef<ScrollViewInstance>(null)

    useEffect(() => {
        if (normalizedInitialEmail) {
            setEmail(normalizedInitialEmail)
        }
    }, [normalizedInitialEmail])

    // Load saved email on mount when a link did not provide an email hint.
    useEffect(() => {
        const loadSavedEmail = async () => {
            if (normalizedInitialEmail) {
                return
            }
            const savedEmail = await getLastEmail()
            if (savedEmail && !email) {
                setEmail(savedEmail)
            }
        }
        loadSavedEmail()
    }, [normalizedInitialEmail])

    useEffect(() => {
        setSelectedMode(resolvedMode);
    }, [resolvedMode]);

    const validateInput = () => {
        setError(null)

        if (requireFirstName) {
            if (!firstName.trim()) {
                setError(msg({ id: 'auth.validation.firstNameRequired', message: 'First name is required' }))
                return false
            }

            if (firstName.trim().length < 2) {
                setError(msg({
                    id: 'auth.validation.firstNameTooShort',
                    message: 'First name must be at least 2 characters',
                }))
                return false
            }

            if (firstName.trim().length > 25) {
                setError(msg({
                    id: 'auth.validation.firstNameTooLong',
                    message: 'First name must be less than 25 characters',
                }))
                return false
            }
        }

        if (!email.trim()) {
            setError(msg({ id: 'auth.validation.emailRequired', message: 'Email is required' }))
            return false
        }

        if (!isValidEmailAddress(email)) {
            setError(msg({
                id: 'auth.validation.emailInvalid',
                message: 'Please enter a valid email address',
            }))
            return false
        }

        return true
    }

    const getErrorMessage = (error: any): MessageDescriptor => {
        // Handle cases where error might not have a proper message
        if (!error || typeof error !== 'object') {
            return msg({
                id: 'auth.error.unexpectedRetry',
                message: 'An unexpected error occurred. Please try again.',
            })
        }
        
        // Extract error message, handling various error object structures
        const errorMessage = error.message || error.msg || error.error_description || error.toString()
        
        // Handle empty or object-like error messages
        if (!errorMessage || errorMessage === '[object Object]' || errorMessage === '{}') {
            // Check for timeout-related properties
            if (error.code === 'TIMEOUT' || error.code === 'ETIMEDOUT' || error.code === 'ECONNABORTED') {
                return msg({
                    id: 'auth.error.timeoutCheckConnection',
                    message: 'The request timed out. Please check your connection and try again.',
                })
            }
            return msg({
                id: 'auth.error.unexpectedRetryLater',
                message: 'An unexpected error occurred. Please try again later.',
            })
        }
        
        // Handle specific error messages
        if (errorMessage.includes('Email not confirmed')) {
            return msg({
                id: 'auth.error.emailNotConfirmed',
                message: 'Please check your email and enter the verification code before signing in.',
            })
        }
        if (errorMessage.includes('User already registered')) {
            return msg({
                id: 'auth.error.accountExists',
                message: 'An account with this email already exists. Try signing in instead.',
            })
        }
        if (errorMessage.includes('Unable to validate email address')) {
            return msg({ id: 'auth.error.invalidEmail', message: 'Please enter a valid email address.' })
        }
        if (errorMessage.includes('fetch failed') || errorMessage.includes('NetworkError')) {
            return msg({
                id: 'auth.error.network',
                message: 'Network error. Please check your internet connection and try again.',
            })
        }
        if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
            return msg({
                id: 'auth.error.timeoutRetry',
                message: 'The request timed out. Please try again.',
            })
        }
        
        // Raw provider/backend errors stay in logs and Sentry; product UI uses a safe fallback.
        return msg({
            id: 'auth.error.generic',
            message: 'Something went wrong. Please try again.',
        })
    }

    const handleSendCode = async () => {
        if (loading) {
            return
        }

        if (!validateInput()) {
            return
        }

        if (resendCooldown > 0) {
            setError(msg({
                id: 'auth.error.resendCooldown',
                message: plural(resendCooldown, {
                    one: 'Please wait one second before requesting another code',
                    other: 'Please wait # seconds before requesting another code',
                }),
            }))
            return
        }

        setLoading(true)
        setError(null)
        setSuccess(null)

        try {
            const normalizedEmail = normalizeEmailAddress(email)

            // Sign out current user if switching accounts
            if (forSwitchAccount) {
                console.log('🔐 [AuthScreen] Switching accounts - signing out current user first')
                await signOutCurrentSession()
            }

            console.log('🔐 [AuthScreen] Sending verification code')
            const signupAttribution = getOrCreateSignupAttribution();
            const e2eContext = getE2ETestRunContext();
            const metadata: Record<string, unknown> = {
                ...getClientSessionMetadata(),
                ...buildExplicitSignupLanguageMetadata({
                    authMode: selectedMode,
                    publicUrlAuthoritative: isPublicUrlLocaleAuthoritative(
                        stripStaticPublicExportPrefix(pathname)
                    ),
                    messageLocale,
                }),
            };

            if (signupAttribution) {
                metadata.signup_attribution = signupAttribution;
            }

            if (e2eContext) {
                metadata.test_run = true;
                metadata.test_run_id = e2eContext.runId;
                metadata.test_run_tag = e2eContext.tag;
            }

            if (requireFirstName) {
                metadata.first_name = firstName.trim();
            }

            const sendError = await sendPasswordlessEmailCode({
                email: normalizedEmail,
                authMode: selectedMode,
                redirectTo,
                funnelContext,
                data: Object.keys(metadata).length > 0 ? metadata : undefined,
                shouldCreateUser: signInOnly ? false : undefined,
            })

            if (sendError) {
                console.log('🔐 [AuthScreen] Verification code error:', sendError)

                captureExceptionIfNotNetwork(new Error(sendError.message || 'Verification code request failed'), {
                    tags: {
                        operation: 'verification_code_request',
                        email: normalizedEmail
                    },
                    extra: {
                        errorMessage: sendError.message,
                        errorCode: sendError.code,
                        errorObject: JSON.stringify(sendError),
                        platform: Platform.OS
                    }
                })
                setError(getErrorMessage(sendError))
            } else {
                await saveLastEmail(normalizedEmail)
                await markAsLoggedIn()
                setEmail(normalizedEmail)
                console.log('✅ [AuthScreen] Saved login state and email after sending verification code')

                setVerificationCodeSent(true)
                setSuccess(msg({
                    id: 'auth.success.verificationCodeSent',
                    message: `Verification code sent to ${normalizedEmail}! Check your email and enter the code.`,
                }))
                startResendCooldown(60)
                onVerificationCodeSent?.()
            }
        } catch (err: any) {
            console.log('🔐 [AuthScreen] Unexpected error:', err)

            captureExceptionIfNotNetwork(err, {
                tags: {
                    operation: 'verification_code_request',
                    email: normalizeEmailAddress(email)
                },
                extra: {
                    errorMessage: err.message,
                    errorCode: err.code,
                    errorObject: JSON.stringify(err),
                    emailLength: email?.length
                }
            })
            setError(getErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }

    const handleTryDifferentEmail = () => {
        setVerificationCodeSent(false)
        setError(null)
        setSuccess(null)
        setEmail('')
        setResendCooldown(0)
    }

    const handleVerificationComplete = async () => {
        try {
            const { data: sessionData, error: sessionError } = await supabase.auth.getSession()

            if (sessionError || !sessionData.session) {
                throw new Error('We could not restore your session. Please try again.')
            }

            await persistPasswordlessSessionState(sessionData.session)

            const outcome = await resolvePasswordlessAuthOutcome({
                authType: selectedMode === 'signup' && !forSwitchAccount ? 'signup' : 'email',
                session: sessionData.session,
                redirectTo,
                funnelContext,
            })

            setConfirmation(outcome.confirmationMessage ?? null)
            closeAuth()
            router.replace(outcome.destination)
        } catch (err: any) {
            setError(getErrorMessage(err))
        }
    }

    const title = verificationCodeSent
        ? msg({ id: 'auth.title.checkEmail', message: 'Check Your Email' })
        : isForwardedAuth
            ? msg({ id: 'auth.title.createResearchTopic', message: 'Create your research topic' })
            : isLegacyOfferAuth
                ? msg({ id: 'auth.title.paperzillaOffer', message: 'Your Paperzilla offer' })
                : msg({ id: 'auth.title.welcome', message: 'Welcome to Paperzilla' });
    const subtitle = verificationCodeSent
        ? isForwardedAuth
            ? msg({
                id: 'auth.subtitle.forwardedCodeSent',
                message: 'Use the code we sent, then Paperzilla will take you to your forwarded-alert setup.',
            })
            : msg({
                id: 'auth.subtitle.codeSent',
                message: 'We sent a verification code to your email',
            })
        : isForwardedAuth
            ? msg({
                id: 'auth.subtitle.forwardedAlertWaiting',
                message: 'Your forwarded alert is waiting in Inbox. Continue with this email to set up the topic that filters future papers.',
            })
            : isLegacyOfferAuth
                ? msg({
                    id: 'auth.subtitle.viewEarlyUserOffer',
                    message: 'Sign in to view your early-user offer',
                })
                : selectedMode === 'signup'
                    ? msg({
                        id: 'auth.subtitle.createAccount',
                        message: 'Create your account with your name and email',
                    })
                    : msg({ id: 'auth.subtitle.signInEmail', message: 'Sign in with your email' });
    const helpText = isForwardedAuth
        ? msg({
            id: 'auth.help.forwardedEmail',
            message: "Use the email address you forwarded from. We'll send you a verification code and then open research-topic setup.",
        })
        : msg({
            id: 'auth.help.passwordless',
            message: "We'll email a verification code so you can continue without a password",
        });

    return (

        <KeyboardAvoidingView
            style={styles.container}
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            keyboardVerticalOffset={Platform.OS === 'ios' ? 120 : 40}
        >
            <ScrollView
                ref={scrollViewRef}
                contentContainerStyle={styles.scrollContainer}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >

                {/* Dynamic Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>
                        {i18n._(title)}
                    </Text>
                    <Text style={styles.subtitle}>
                        {i18n._(subtitle)}
                    </Text>
                </View>

                {error && (
                    <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>{i18n._(error)}</Text>
                    </View>
                )}

                {success && (
                    <View style={styles.successContainer}>
                        <Text style={styles.successText}>{i18n._(success)}</Text>
                    </View>
                )}

                {/* First Name Field */}
                {!verificationCodeSent && (
                    requireFirstName && (
                        <TextInput
                            ref={firstNameRef}
                            style={[styles.input, error && styles.inputError]}
                            placeholder={i18n._(msg({
                                id: 'auth.field.firstNamePlaceholder',
                                message: 'Enter your first name',
                            }))}
                            testID="auth-first-name-input"
                            value={firstName}
                            onChangeText={(text) => {
                                setFirstName(text)
                                if (error) setError(null)
                            }}
                            autoCapitalize="words"
                            editable={!loading}
                            returnKeyType="next"
                            onSubmitEditing={() => emailRef.current?.focus()}
                            textContentType="givenName"
                            maxLength={25}
                        />
                    )
                )}

                {/* Email Field */}
                {!verificationCodeSent && (

                    <TextInput
                        ref={emailRef}
                        style={[styles.input, error && styles.inputError]}
                        placeholder={i18n._(msg({
                            id: 'auth.field.emailPlaceholder',
                            message: 'Enter your email address',
                        }))}
                        testID="auth-email-input"
                        value={email}
                        onChangeText={(text) => {
                            setEmail(text)
                            if (error) setError(null)
                        }}
                        autoCapitalize="none"
                        keyboardType="email-address"
                        editable={!loading}
                        returnKeyType="go"
                        onSubmitEditing={handleSendCode}
                        autoComplete="email"
                        textContentType="emailAddress"
                        importantForAutofill="yes"
                    />
                )}

                {!verificationCodeSent && isForwardedAuth && (
                    <Text style={styles.emailFieldHelp}>
                        {i18n._(helpText)}
                    </Text>
                )}

                {/* OTP Code Entry State */}
                {verificationCodeSent && (
                    <View style={styles.verificationCodeSentContainer}>
                        <VerificationCodePrompt
                            email={email}
                            emailLabel={msg({ id: 'auth.code.emailSentTo', message: 'Email sent to:' })}
                            instructionText={msg({
                                id: 'auth.code.instructions',
                                message: 'Enter the latest 6-digit code from your email to continue.',
                            })}
                            onVerified={handleVerificationComplete}
                            resendCooldown={resendCooldown}
                            resendLoading={loading}
                            onResend={handleSendCode}
                            resendTestID="auth-resend-code-button"
                            secondaryActionLabel={msg({
                                id: 'auth.code.useDifferentEmail',
                                message: 'Use a different email',
                            })}
                            onSecondaryAction={handleTryDifferentEmail}
                            secondaryActionTestID="auth-use-different-email-button"
                        />
                    </View>
                )}

                {/* Continue Button */}
                {!verificationCodeSent && (
                    <TouchableOpacity
                        style={[styles.button, loading && styles.buttonDisabled]}
                        onPress={handleSendCode}
                        disabled={loading}
                        testID="auth-submit-button"
                    >
                        {loading ? (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size="small" color="white" />
                                <Text style={styles.buttonText}><Trans id="authscreen.sendingCode" comment="UI copy in components/AuthScreen.tsx">
                                    Sending code...
                                </Trans></Text>
                            </View>
                        ) : (
                            <Text style={styles.buttonText}>
                                {isForwardedAuth ? <Trans id="authscreen.continue" comment="Conditional UI copy in components/AuthScreen.tsx">Continue</Trans> : selectedMode === 'signup' ? <Trans id="authscreen.createAccount" comment="Conditional UI copy in components/AuthScreen.tsx">Create account</Trans> : <Trans id="authscreen.signIn" comment="Conditional UI copy in components/AuthScreen.tsx">Sign in</Trans>}
                            </Text>
                        )}
                    </TouchableOpacity>
                )}

                {!verificationCodeSent && isForwardedAuth && (
                    <View style={styles.forwardedContactRow}>
                        <FontAwesome6
                            name="triangle-exclamation"
                            size={13}
                            color="#b45309"
                            style={styles.forwardedContactIcon}
                        />
                        <Text style={styles.forwardedContactText}><Trans id="authscreen.attentionIfYouAreAlreadyUsingPaperzillaWithAnotherEmail" comment="UI copy in components/AuthScreen.tsx">
                            Attention: If you are already using Paperzilla with another email address, please contact us
                            and we will connect the dots: hello@paperzilla.ai
                        </Trans></Text>
                    </View>
                )}

                {!verificationCodeSent && !isForwardedAuth && !forSwitchAccount && !signInOnly && (
                    <TouchableOpacity
                        style={styles.modeSwitchButton}
                        onPress={() => {
                            setSelectedMode((prev) => (prev === 'signup' ? 'signin' : 'signup'));
                            setError(null);
                            setSuccess(null);
                        }}
                        disabled={loading}
                        testID="auth-switch-mode-button"
                    >
                        <Text style={styles.modeSwitchText}>
                            {selectedMode === 'signup' ? <Trans id="authscreen.alreadyHaveAnAccountSignIn" comment="Conditional UI copy in components/AuthScreen.tsx">Already have an account? Sign in</Trans> : <Trans id="authscreen.needAnAccountCreateOne" comment="Conditional UI copy in components/AuthScreen.tsx">Need an account? Create one</Trans>}
                        </Text>
                    </TouchableOpacity>
                )}

                {!verificationCodeSent && !isForwardedAuth && (
                    <Text style={styles.helpText}>
                        {i18n._(helpText)}
                    </Text>
                )}
            </ScrollView>
        </KeyboardAvoidingView>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#fafafa',
        ...(Platform.OS === 'web' && {
            maxWidth: 900,
            alignSelf: 'center',
            width: '100%',
            marginHorizontal: 'auto',
        }),
    },
    scrollContainer: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingBottom: 150,
        paddingHorizontal: 20,
    },
    header: {
        marginBottom: 32,
        paddingVertical: 20,
        paddingHorizontal: 16,
        borderRadius: 12,
        alignItems: 'center',
        backgroundColor: '#f8fffe', // Very subtle green tint
        borderWidth: 1,
        borderColor: colors.primaryGreenLight,
    },
    subtitle: {
        fontSize: 16,
        color: colors.primaryGreenDark, // Better contrast
        textAlign: 'center',
        lineHeight: 22,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 8,
        textAlign: 'center',
    },
    emailFieldHelp: {
        fontSize: 14,
        color: '#6b7280',
        lineHeight: 20,
        marginTop: -4,
        marginBottom: 18,
    },
    errorContainer: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fecaca',
        borderRadius: 8,
        padding: 12,
        marginBottom: 15,
    },
    errorText: {
        color: '#dc2626',
        fontSize: 14,
        textAlign: 'center',
    },
    successContainer: {
        backgroundColor: '#f0fdf4',
        borderWidth: 1,
        borderColor: '#bbf7d0',
        borderRadius: 8,
        padding: 12,
        marginBottom: 15,
    },
    successText: {
        color: colors.primaryGreen,
        fontSize: 14,
        textAlign: 'center',
        fontWeight: '500',
    },
    input: {
        borderWidth: 1,
        borderColor: '#ddd',
        padding: 15,
        marginBottom: 15,
        borderRadius: 8,
        fontSize: 16,
        backgroundColor: 'white',
    },
    inputError: {
        borderColor: '#dc2626',
        borderWidth: 1.5,
    },
    verificationCodeSentContainer: {
        alignItems: 'center',
        padding: 20,
        backgroundColor: colors.bgWeak,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border,
        marginBottom: 20,
    },
    button: {
        backgroundColor: colors.primaryGreen,
        paddingHorizontal: 32,
        paddingVertical: 14,
        borderRadius: 8,
        alignSelf: 'center',
        alignItems: 'center',
        marginBottom: 15,
        minHeight: 50,
        justifyContent: 'center',
    },
    buttonDisabled: {
        backgroundColor: colors.paleGreen,
    },
    buttonText: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: 16,
    },
    loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    helpText: {
        fontSize: 14,
        color: '#6b7280',
        textAlign: 'center',
        marginTop: 16,
        lineHeight: 20,
    },
    forwardedContactRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'center',
        marginTop: 2,
        paddingHorizontal: 4,
    },
    forwardedContactIcon: {
        marginRight: 8,
        marginTop: 3,
    },
    forwardedContactText: {
        flex: 1,
        fontSize: 14,
        color: colors.text700,
        lineHeight: 20,
    },
    modeSwitchButton: {
        alignItems: 'center',
        marginTop: 4,
    },
    modeSwitchText: {
        fontSize: 14,
        color: colors.text700,
        textDecorationLine: 'underline',
    },
})
